import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  getDoc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/AgentNavbar";
import { FaEdit, FaTrash, FaPlus, FaGift, FaDollarSign, FaUndo } from "react-icons/fa";

const transactionTypes = [
  { label: "New Investment", value: "New Investment", icon: <FaDollarSign /> },
  { label: "Bonus", value: "Bonus", icon: <FaGift /> },
  { label: "Fee Refund", value: "Fee Refund", icon: <FaUndo /> },
];

export default function EditTransactions() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [newDescription, setNewDescription] = useState("");
  const [newStatus, setNewStatus] = useState("Pending");
  const [addFormVisible, setAddFormVisible] = useState(false);
  const [newTxnType, setNewTxnType] = useState(transactionTypes[1]);
  const [newAmount, setNewAmount] = useState("");
  const [newTxnDescription, setNewTxnDescription] = useState("");

  // Fetch clients
  useEffect(() => {
    const fetchClients = async () => {
      const querySnapshot = await getDocs(collection(db, "users"));
      let clients = querySnapshot.docs
        .filter((doc) => doc.data().role === "client")
        .map((doc) => ({ id: doc.id, ...doc.data() }));

      const LOGGED_IN_AGENT_USERNAME = localStorage.getItem("currentUser") || "";
      if (LOGGED_IN_AGENT_USERNAME !== "buksna") {
        clients = clients.filter(
          (client) => client.createdBy === LOGGED_IN_AGENT_USERNAME
        );
      }

      setClients(clients);
    };

    fetchClients();
  }, []);

  // Fetch transactions for selected client
  useEffect(() => {
    if (!selectedClientId) return;

    const q = query(
      collection(db, "users", selectedClientId, "withdrawals"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const txns = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          amount: data.amount,
          balanceAfter: data.balanceAfter || 0,
          description: data.description || "",
          type: data.type,
          status: data.status || "Pending",
          timestamp: data.timestamp?.toDate(),
        };
      });

      setTransactions(txns);
    });

    return () => unsubscribe();
  }, [selectedClientId]);

  // --- Transaction actions ---

  const openEditModal = (txn) => {
    setEditingTxn(txn);
    setNewDescription(txn.description);
    setNewStatus(txn.status);
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingTxn || !selectedClientId) return;

    const txnRef = doc(db, "users", selectedClientId, "withdrawals", editingTxn.id);
    await updateDoc(txnRef, { description: newDescription, status: newStatus });
    setEditModalOpen(false);
  };

  // Delete transaction with rollback of currentBalance
  const handleDelete = async (txnId) => {
    if (!selectedClientId || !txnId) return;
    const confirmDelete = window.confirm("Are you sure you want to delete this transaction?");
    if (!confirmDelete) return;

    const userRef = doc(db, "users", selectedClientId);
    const withdrawalsRef = collection(db, "users", selectedClientId, "withdrawals");

    // Delete transaction
    await deleteDoc(doc(withdrawalsRef, txnId));

    // Recalculate currentBalance based on startingBalance + all remaining transactions
    const userSnap = await getDoc(userRef);
    const startingBalance = userSnap.data()?.startingBalance || 0;

    const txnSnapshot = await getDocs(withdrawalsRef);
    const sortedTxns = txnSnapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => a.timestamp?.toDate() - b.timestamp?.toDate());

    let runningBalance = startingBalance;
    for (const txn of sortedTxns) {
      runningBalance += txn.amount;
      await updateDoc(doc(withdrawalsRef, txn.id), { balanceAfter: runningBalance });
    }

    await updateDoc(userRef, { currentBalance: runningBalance });
  };

  // Add transaction
  const handleAddTransaction = async () => {
    if (!newAmount || isNaN(newAmount)) return;

    const numericAmount = parseFloat(newAmount);

    const userRef = doc(db, "users", selectedClientId);
    const userSnap = await getDoc(userRef);
    const startingBalance = userSnap.data()?.startingBalance || 0;
    const currentBalance = userSnap.data()?.currentBalance || startingBalance;

    const newBalance = currentBalance + numericAmount;

    await addDoc(collection(db, "users", selectedClientId, "withdrawals"), {
      amount: numericAmount,
      balanceAfter: newBalance,
      description: newTxnDescription,
      type: newTxnType.value,
      status: "Completed",
      timestamp: serverTimestamp(),
    });

    await updateDoc(userRef, { currentBalance: newBalance });

    setAddFormVisible(false);
    setNewAmount("");
    setNewTxnDescription("");
    setNewTxnType(transactionTypes[1]);
  };

  // Currency formatter
  const formatCurrency = (amount, type) =>
    type === "Withdrawal"
      ? `-$${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
      : `$${Math.abs(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <div className="dashboard-container" style={{ background: "#f9fafb", minHeight: "100vh" }}>
      <Navbar />
      <div style={{ padding: "1rem 2rem 2rem", maxWidth: "1100px", margin: "20px auto 0" }}>
        <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "0.5rem" }}>
          Edit Client Transactions
        </h2>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>
          Select a client to view and edit their transaction history.
        </p>

        <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginBottom: "1rem" }}>
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            style={{
              padding: "0.6rem",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              width: "100%",
              maxWidth: "400px",
              fontSize: "1rem",
            }}
          >
            <option value="">Select a client...</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.fullName} ({client.id})
              </option>
            ))}
          </select>

          {selectedClientId && (
            <button
              onClick={() => setAddFormVisible((v) => !v)}
              style={{
                backgroundColor: "#0080ff",
                color: "white",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                cursor: "pointer",
              }}
            >
              <FaPlus /> Add Transaction
            </button>
          )}
        </div>

        {/* Add Form */}
        {addFormVisible && (
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "1.5rem",
              marginBottom: "2rem",
              maxWidth: "500px",
            }}
          >
            <label style={{ display: "block", marginBottom: "0.75rem" }}>
              <strong>Add Transaction</strong>
            </label>

            <div style={{ marginBottom: "1rem" }}>
              <select
                value={newTxnType.value}
                onChange={(e) =>
                  setNewTxnType(transactionTypes.find((t) => t.value === e.target.value))
                }
                style={{
                  width: "95%",
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                }}
              >
                {transactionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label>Amount</label>
              <input
                type="number"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
                placeholder="$ 0.00"
                style={{
                  width: "90%",
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                }}
              />
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label>Description (Optional)</label>
              <textarea
                value={newTxnDescription}
                onChange={(e) => setNewTxnDescription(e.target.value)}
                placeholder={`Enter details for ${newTxnType.label}...`}
                rows={3}
                style={{
                  width: "90%",
                  padding: "0.75rem",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                }}
              />
            </div>

            <button
              onClick={handleAddTransaction}
              style={{
                backgroundColor: "#0080ff",
                color: "white",
                border: "none",
                padding: "0.5rem 1rem",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FaPlus /> Submit Transaction
            </button>
          </div>
        )}

        {/* Transaction Table */}
        {selectedClientId && (
          <div
            style={{
              backgroundColor: "#fff",
              padding: "1.5rem",
              borderRadius: "10px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            }}
          >
            <h3 style={{ fontSize: "1.2rem", marginBottom: "1rem", color: "#1f2937" }}>
              Transactions for {clients.find((c) => c.id === selectedClientId)?.fullName}
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: "0.9rem", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f3f4f6", textAlign: "left" }}>
                    <th style={{ padding: "0.75rem" }}>Date</th>
                    <th style={{ padding: "0.75rem" }}>Type</th>
                    <th style={{ padding: "0.75rem" }}>Description</th>
                    <th style={{ padding: "0.75rem" }}>Amount</th>
                    <th style={{ padding: "0.75rem" }}>Balance After</th>
                    <th style={{ padding: "0.75rem" }}>Status</th>
                    <th style={{ padding: "0.75rem" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => {
                    const isWithdrawal = txn.type === "Withdrawal";
                    return (
                      <tr key={txn.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "0.75rem" }}>
                          {txn.timestamp?.toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td style={{ padding: "0.75rem" }}>{txn.type}</td>
                        <td style={{ padding: "0.75rem" }}>{txn.description}</td>
                        <td
                          style={{
                            padding: "0.75rem",
                            color: isWithdrawal ? "#dc2626" : "#16a34a",
                            fontWeight: "bold",
                          }}
                        >
                          {formatCurrency(txn.amount, txn.type)}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          ${txn.balanceAfter.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: "0.75rem" }}>{txn.status}</td>
                        <td style={{ padding: "0.75rem" }}>
                          <button
                            onClick={() => openEditModal(txn)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#2563eb",
                              marginRight: "0.5rem",
                            }}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDelete(txn.id)}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#dc2626",
                            }}
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              padding: "2rem",
              width: "100%",
              maxWidth: "480px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginBottom: "0.5rem", fontSize: "1.25rem" }}>Edit Transaction</h3>
            <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: "1rem" }}>
              Modify the transaction description below.
            </p>
            <label style={{ display: "block", marginBottom: "0.5rem" }}>
              Amount:
              <input
                type="text"
                value={formatCurrency(editingTxn.amount, editingTxn.type)}
                disabled
                style={{
                  width: "100%",
                  marginTop: "0.25rem",
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f3f4f6",
                }}
              />
            </label>
            <label style={{ display: "block", marginTop: "1rem" }}>
              Description:
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  marginTop: "0.25rem",
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                }}
              />
            </label>
            <label style={{ display: "block", marginTop: "1rem" }}>
              Status:
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                style={{
                  width: "100%",
                  marginTop: "0.25rem",
                  padding: "0.5rem",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                }}
              >
                <option value="Completed">Completed</option>
                <option value="Pending">Pending</option>
              </select>
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem", gap: "0.5rem" }}>
              <button
                onClick={() => setEditModalOpen(false)}
                style={{
                  backgroundColor: "#e5e7eb",
                  border: "none",
                  padding: "0.5rem 1rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  border: "none",
                  padding: "0.5rem 1rem",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
